import { SyncOperation } from "../shared/api.js";
import { getAuthUser } from "./auth0.js";
import { needsSignIn, ReadingBeeApi, readingBeeApi } from "./sql-api.js";
import { AppStore } from "./store.js";
import { OutboxRecord, SqlOutbox } from "./sql-outbox.js";
import { createId } from "../shared/util.id.js";

export type SqlSyncStatus = {
  pending: number;
  busy: boolean;
  error: string | null;
  needsSignIn: boolean;
  lastSyncedAt: string | null;
};

export const sqlSyncSession = new EventTarget();

let status: SqlSyncStatus = { pending: 0, busy: false, error: null, needsSignIn: false, lastSyncedAt: null };
let lastSyncFailure: unknown = null;
let activeStore: AppStore | null = null;
let outbox: SqlOutbox | null = null;
let api: ReadingBeeApi = readingBeeApi;
let chain: Promise<void> = Promise.resolve();
let listenersInstalled = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

export const getSqlOutbox = (): SqlOutbox => {
  outbox ??= new SqlOutbox();
  return outbox;
};

const setStatus = (patch: Partial<SqlSyncStatus>): void => {
  status = { ...status, ...patch };
  sqlSyncSession.dispatchEvent(new Event("change"));
};

export const getSqlSyncStatus = (): SqlSyncStatus => status;

const isOffline = (): boolean => typeof navigator !== "undefined" && navigator.onLine === false;

const enqueue = (work: () => Promise<void>): Promise<void> => {
  const next = chain.then(work, work);
  chain = next.then(
    () => undefined,
    () => undefined,
  );
  return next;
};

const operationsFrom = (event: Event): SyncOperation[] => {
  if (!(event instanceof CustomEvent)) return [];
  const detail = event.detail as { operations?: unknown } | undefined;
  return Array.isArray(detail?.operations) ? (detail.operations as SyncOperation[]) : [];
};

const refreshPending = async (): Promise<void> => {
  if (!outbox) return;
  setStatus({ pending: await outbox.count() });
};

const flush = async (): Promise<void> => {
  if (!outbox || !getAuthUser() || isOffline()) {
    await refreshPending();
    return;
  }
  setStatus({ busy: true, error: null, needsSignIn: false });
  lastSyncFailure = null;
  try {
    while (true) {
      const records = await outbox.readyBatch();
      if (records.length === 0) break;
      const response = await api.syncBatch({
        clientId: await outbox.clientId(),
        operations: records.map((record) => record.operation),
      });
      await outbox.acknowledge([...response.accepted, ...response.duplicates]);
      if (response.rejected.length > 0) {
        await outbox.defer(
          response.rejected.map((item) => item.eventId),
          response.rejected.map((item) => `${item.code}: ${item.message}`).join("; "),
        );
        setStatus({ error: "Some Reading Bee updates need attention" });
        break;
      }
    }
    setStatus({ pending: await outbox.count(), lastSyncedAt: new Date().toISOString() });
  } catch (error) {
    await outbox.defer(
      (await outbox.readyBatch()).map((record) => record.eventId),
      error,
    );
    scheduleRetry();
    lastSyncFailure = error;
    setStatus({
      pending: await outbox.count(),
      error: error instanceof Error ? error.message : "Could not sync",
      needsSignIn: needsSignIn(error),
    });
  } finally {
    setStatus({ busy: false });
  }
};

const onPersist = (event: Event): void => {
  const operations = operationsFrom(event);
  if (!outbox || operations.length === 0) return;
  void enqueue(async () => {
    const profiles = operations
      .map((operation) => activeStore!.state.profiles.find((profile) => profile.id === operation.studentId))
      .filter((profile): profile is NonNullable<typeof profile> => Boolean(profile));
    await outbox!.persistWithProjection(
      operations,
      profiles,
      activeStore!.state.currentProfileId,
      activeStore!.state.incomingShares,
    );
    await flush();
  });
};

const onOnline = (): void => {
  void enqueue(flush);
};

const onVisibility = (): void => {
  if (typeof document !== "undefined" && document.visibilityState === "visible") void enqueue(flush);
};

const scheduleRetry = async (): Promise<void> => {
  if (retryTimer) clearTimeout(retryTimer);
  if (!outbox) return;
  const records = await outbox.pending();
  const next = records
    .map((record) => record.nextAttemptAt)
    .filter((time) => time > Date.now())
    .sort((a, b) => a - b)[0];
  if (next === undefined) return;
  retryTimer = setTimeout(() => void enqueue(flush), Math.max(0, next - Date.now()));
};

export const startSqlSync = (store: AppStore, options?: { api?: ReadingBeeApi }): void => {
  activeStore?.removeEventListener("persist", onPersist);
  activeStore = store;
  outbox = getSqlOutbox();
  api = options?.api ?? readingBeeApi;
  store.addEventListener("persist", onPersist);
  if (!listenersInstalled && typeof window !== "undefined") {
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    listenersInstalled = true;
  }
  void refreshPending();
};

export const flushSqlSync = async (): Promise<void> => {
  await enqueue(flush);
};

const queueLocalProfiles = async (): Promise<void> => {
  if (!outbox || !activeStore) return;
  const pendingStudentIds = await outbox.pendingStudentIds();
  const profiles = activeStore.state.profiles;
  const operations = profiles
    .filter((profile) => !pendingStudentIds.has(profile.id) && !activeStore!.isIncomingShare(profile.id))
    .flatMap((profile) => [
      {
        eventId: createId(),
        type: "student.created" as const,
        studentId: profile.id,
        occurredAt: profile.createdAt,
        payload: {
          name: profile.name,
          colorPairIndex: profile.colorPairIndex,
          readingBand: profile.band,
        },
      },
      ...(["reading", "math", "spelling"] as const).map((subject) => ({
        eventId: createId(),
        type: "subject.current.updated" as const,
        studentId: profile.id,
        occurredAt: new Date().toISOString(),
        payload: {
          subject,
          currentTextId:
            subject === "reading"
              ? profile.currentTextId
              : subject === "math"
                ? (profile.subjectProgress?.math.currentTextId ?? null)
                : (profile.spellingWordId ?? null),
        },
      })),
    ]);
  if (operations.length === 0) return;
  await outbox.persistWithProjection(
    operations,
    profiles,
    activeStore.state.currentProfileId,
    activeStore.state.incomingShares,
  );
};

export const hydrateSqlSync = async (store: AppStore): Promise<void> => {
  if (!getAuthUser() || isOffline()) return;
  const initial = await api.bootstrap();
  if (initial.students.length === 0) {
    await queueLocalProfiles();
    await flushSqlSync();
    await refreshPending();
    return;
  }
  const remoteStudentIds = new Set(initial.students.map((student) => student.id));
  await getSqlOutbox().retainOperationsForStudents(remoteStudentIds);
  await flushSqlSync();
  const syncError = getSqlSyncStatus().error;
  if (syncError) throw lastSyncFailure instanceof Error ? lastSyncFailure : new Error(syncError);
  const bootstrap = await api.bootstrap();
  await clearSqlLocalData();
  store.applyRemoteSummaries(bootstrap.students, bootstrap.shares);
  await refreshPending();
};

export const pendingOutboxRecords = async (): Promise<OutboxRecord[]> => (outbox ? outbox.pending() : []);

export const clearSqlLocalData = async (): Promise<void> => {
  await getSqlOutbox().clearLocalData();
  setStatus({ pending: 0, error: null, needsSignIn: false, lastSyncedAt: null });
  lastSyncFailure = null;
};

export const removeSqlProfileData = async (profileId: string): Promise<void> => {
  await getSqlOutbox().removeProfileData(profileId);
  await refreshPending();
};
